import React, { useEffect, useState } from "react";
import { Storefront } from "@phosphor-icons/react";
import api from "@/lib/api";

const RANGES = [7, 30, 90];
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${Math.round(Number(n || 0) * 100)}%`;
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const CHIP = {
  listed: "bg-sky-100 border-sky-700 text-sky-900",
  sold: "bg-emerald-100 border-emerald-700 text-emerald-800",
  expired: "bg-zinc-100 border-zinc-500 text-zinc-700",
};

const Tile = ({ label, value, testId, hint }) => (
  <div className="border-2 border-black bg-white p-3" data-testid={testId}>
    <div className="label-mono">{label}</div>
    <div className="font-chivo font-black text-2xl mt-1">{value}</div>
    {hint && <div className="text-[10.5px] text-zinc-600 mt-0.5">{hint}</div>}
  </div>
);

const Tiles = ({ t }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="vendor-mkt-tiles">
    <Tile label="In marketplace now" value={t.in_marketplace_now} testId="vendor-mkt-now" hint="waiting for a buyer" />
    <Tile label="Entered marketplace" value={t.listed} testId="vendor-mkt-listed" />
    <Tile label="Sold" value={t.sold} testId="vendor-mkt-sold" />
    <Tile label="Sell-through" value={pct(t.sell_through)} testId="vendor-mkt-sell-through" />
    <Tile label="Commission earned" value={usd(t.commission)} testId="vendor-mkt-commission" />
  </div>
);

const StateTable = ({ rows }) => (
  <div className="overflow-x-auto border-2 border-black bg-white" data-testid="vendor-mkt-by-state">
    <table className="w-full text-xs">
      <thead><tr className="bg-zinc-100 text-[10px] uppercase tracking-widest"><th className="text-left px-3 py-2">State</th><th className="text-right px-3 py-2">Entered</th><th className="text-right px-3 py-2">Waiting</th><th className="text-right px-3 py-2">Sold</th><th className="text-right px-3 py-2">Commission</th></tr></thead>
      <tbody>
        {rows.slice(0, 10).map((r) => (
          <tr key={r.state} className="border-t border-zinc-200" data-testid={`vendor-mkt-state-${r.state}`}>
            <td className="px-3 py-1.5 font-bold">{r.state}</td><td className="px-3 py-1.5 text-right">{r.listed}</td>
            <td className="px-3 py-1.5 text-right">{r.in_marketplace}</td><td className="px-3 py-1.5 text-right">{r.sold}</td>
            <td className="px-3 py-1.5 text-right font-mono">{usd(r.commission)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RecentTable = ({ rows }) => (
  <div className="overflow-x-auto border-2 border-black bg-white max-h-72 overflow-y-auto" data-testid="vendor-mkt-recent">
    <table className="w-full text-xs">
      <thead><tr className="bg-zinc-100 text-[10px] uppercase tracking-widest"><th className="text-left px-3 py-2">Lead</th><th className="text-left px-3 py-2">State</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Listed</th><th className="text-right px-3 py-2">Commission</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.reference} className="border-t border-zinc-200" data-testid={`vendor-mkt-row-${r.reference}`}>
            <td className="px-3 py-1.5 font-mono">{r.reference}</td><td className="px-3 py-1.5">{r.state || "—"}</td>
            <td className="px-3 py-1.5"><span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${CHIP[r.status]}`}>{r.status_label}</span></td>
            <td className="px-3 py-1.5">{when(r.listed_at)}</td>
            <td className="px-3 py-1.5 text-right font-mono">{r.commission == null ? "—" : usd(r.commission)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function VendorMarketplaceStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/vendor/marketplace", { params: { days } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [days]);
  return (
    <section className="brut-card p-4 md:p-5 mb-6" data-testid="vendor-marketplace-stats">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Storefront size={16} weight="bold" />
        <h2 className="font-chivo font-black uppercase tracking-tighter">Your leads in the marketplace</h2>
        <div className="sm:ml-auto flex gap-1.5">
          {RANGES.map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`vendor-mkt-range-${d}`}
                    className={`border-2 border-black px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors ${days === d ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
              {d} days
            </button>
          ))}
        </div>
      </div>
      {data?.commission_pct != null && (
        <p className="text-xs text-zinc-600 mb-3" data-testid="vendor-mkt-rate">
          You earn <b>{data.commission_pct}%</b> of the sale price each time one of your leads sells in the marketplace.
        </p>
      )}
      {!data && <div className="label-mono">Loading…</div>}
      {data?.error && <div className="text-xs text-red-700" data-testid="vendor-mkt-error">Couldn&apos;t load marketplace stats.</div>}
      {data?.totals && (
        <div className="flex flex-col gap-3">
          <Tiles t={data.totals} />
          {data.recent.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3"><StateTable rows={data.by_state} /><RecentTable rows={data.recent} /></div>
          ) : <div className="text-xs text-zinc-500" data-testid="vendor-mkt-empty">None of your leads entered the marketplace in this date range.</div>}
        </div>
      )}
    </section>
  );
}
